import React, { useEffect, useRef, useState } from 'react'
import i18next from 'i18next'
import { Trans } from 'react-i18next'
import { fromImage } from 'imtool'
import { useDropzone } from 'react-dropzone'
import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Thumbnail, { ThumbnailDefaultSize } from 'ui/partials/Thumbnail'
import { Caption, Text1 } from 'ui/shorthands/Texts'
import { fileToDataUrl, heicToJpg, isHeic, isValidFileType } from 'ui/utils/imageUtils'
import { ImageProps } from 'types/common'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'hidden',
  },
  description: {
    paddingBottom: theme.spacing(2),
  },
  dropzone: {
    width: ThumbnailDefaultSize,
    height: ThumbnailDefaultSize + 32,
    justifyContent: 'space-around',
    display: 'flex',
    alignItems: 'center',
  },
  dropzoneText: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dropzoneTextTitle: {
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
    paddingBottom: theme.spacing(0.5),
  },
  required: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
  },
  highlightText: {
    paddingTop: theme.spacing(1.5),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
}))

type ThumbnailListProps = {
  images: ImageProps[]
}

export const ThumbnailList: React.FC<ThumbnailListProps> = ({
  images,
}: ThumbnailListProps) => {
  const { classes } = useStyles()

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        {images.map((image, index) => (
          <Grid item key={`${image.file_name}${index}`}>
            <Thumbnail image={image} />
          </Grid>
        ))}
      </Grid>
    </div>
  )
}

type EditableThumbnailListProps = {
  images: ImageProps[]
  onChangeImages: (data: ImageProps[], changed?: boolean) => void
  maxLength?: number
  maxFileSize?: number
  maxImageSize?: number // TODO: 画像リサイズはいったん送信にする
  description?: React.ReactNode
  injectedErrorMessage?: React.ReactNode
  reloadable?: boolean
  required?: boolean
}

const errorText = {
  max: (size: number) =>
    i18next.t(
      'errors.ファイルの数を減らして、もう1度お試しください。アップロードできるファイルは最大{{size}}点です。',
      { size }
    ),
  size: (size: number) =>
    i18next.t(
      'errors.ファイルサイズを削減して、もう1度お試しください。アップロードできるファイルは1点{{size}}MBまでです。',
      { size: Math.round((size / (1024 * 1024)) * 10) / 10 }
    ),
}

const resizeImageToDataUrl: (
  file: File,
  maxSize?: number
) => Promise<string | undefined> = async (file, maxSize) => {
  const tool = await fromImage(file)
  if (maxSize && (tool.width >= maxSize || tool.height >= maxSize)) {
    return await tool.thumbnail(maxSize).toDataURL()
  } else {
    return await fileToDataUrl(file)
  }
}

export const EditableThumbnailList: React.FC<EditableThumbnailListProps> = ({
  images,
  maxLength,
  description,
  maxFileSize,
  onChangeImages,
  maxImageSize,
  injectedErrorMessage,
  reloadable,
  required,
}: EditableThumbnailListProps) => {
  const { classes, cx } = useStyles()
  const [uploadable, setUploadable] = useState<boolean>(true)
  const [maxCountError, setMaxCountError] = useState<boolean>(false)
  const [maxFileSizeError, setMaxFileSizeError] = useState<boolean>(false)
  const [fileTypeError, setFileTypeError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )
  // NOTE: 新規アップロードファイルのみ型がFileになる
  const [files, setFiles] = useState<(File | ImageProps)[]>([])

  const onChangeImagesRef = useRef(onChangeImages)

  const acceptFileTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    //TODO: windowsでheicが使えるか確認
    'image/heif': ['.heic', '.heif'],
    'image/heic': ['.heic', '.heif'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
  }
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptFileTypes,
    onDrop: async (acceptedFiles) => {
      /**
       * NOTE: ファイル数バリデーション
       */
      const count = acceptedFiles.length + files.length
      if (maxLength && count > maxLength) {
        setMaxCountError(true)
        return
      }
      setMaxCountError(false)
      /**
       * NOTE: ファイルサイズバリデーション
       */
      const overFileSizeFiles: Array<File> = acceptedFiles.filter((file) => {
        if (!maxFileSize) {
          return true
        }
        return file.size > maxFileSize
      })
      if (overFileSizeFiles.length) {
        setMaxFileSizeError(true)
        return
      }
      setMaxFileSizeError(false)

      /**
       * NOTE: ファイルの拡張子とMIMEタイプの一致をチェック
       */
      // 全てのファイルをチェック
      let hasInvalidFile = false;
      for (const file of acceptedFiles) {
        try {
          const isValid = await isValidFileType(file)
          if (!isValid) {
            hasInvalidFile = true;
            break;
          }
        } catch (error) {
          console.error('File validation error:', error);
          hasInvalidFile = true;
          break;
        }
      }

      if (hasInvalidFile) {
        setFileTypeError(true)
        return
      }
      setFileTypeError(false)

      /**
       * NOTE: 変換するファイルと変換しないファイルに分けてfilesにセット
       */
      const noHeicFiles = acceptedFiles.filter((file) => !isHeic(file))
      const noConvertFiles = noHeicFiles.map((file) => {
        return Object.assign(file, {
          revokeObjectURL: URL.createObjectURL(file),
        })
      })
      setFiles((prev) => [...prev, ...noConvertFiles])
      // heicのみ非同期でjpgに変換してセット
      const heicFiles = acceptedFiles.filter(isHeic)
      heicFiles.forEach((file) => {
        heicToJpg(file, (jpg) => {
          Object.assign(jpg, {
            revokeObjectURL: URL.createObjectURL(jpg),
          })
          setFiles((prev) => [...prev, jpg])
        })
      })
    },
  })

  useEffect(() => {
    if (maxLength && maxCountError) {
      setErrorMessage(errorText.max(maxLength))
    }
    if (maxFileSize && maxFileSizeError) {
      setErrorMessage(errorText.size(maxFileSize))
    }
    if (fileTypeError) {
      setErrorMessage(i18next.t('errors.ファイルの拡張子とファイルタイプが一致しません。正しいファイルをアップロードしてください。'))
    }
    if (!maxCountError && !maxFileSizeError && !fileTypeError) {
      setErrorMessage(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxCountError, maxFileSizeError, fileTypeError, errorMessage])

  const [isMounted, setIsMounted] = useState(false)

  const [isEmptied, setIsEmptied] = useState(false)

  useEffect(() => {
    if (images.length) {
      setIsMounted(false)
      Promise.allSettled(
        images.map((image) => {
          return setFiles((prev) => [...prev, image])
        })
      ).then(() => setIsMounted(true))
    } else {
      setIsMounted(true)
      setFiles([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length])

  useEffect(() => {
    if (isMounted && reloadable) {
      if (images.length === 0) {
        setFiles([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images])

  const onDeleteHandler = (index: number) => {
    setIsEmptied(true)
    const temp = [...files]
    temp.splice(index, 1)
    setFiles(temp)
  }

  useEffect(() => {
    const count = files.length
    if (maxLength && count < maxLength) {
      setUploadable(true)
    } else {
      setUploadable(false)
    }

    if (!isMounted) return // NOTE: マウント時にonChangeImagesが呼ばれるのを防ぐ

    /**
     * NOTE: 非同期画像リサイズ&base64プロセス
     */
    async function setConvertedImages() {
      const result = await Promise.all(
        files.map(async (file) => {
          if (file instanceof File) {
            if (file.type !== 'image/jpeg') {
              return await fileToDataUrl(file)
            }
            try {
              return await resizeImageToDataUrl(file, maxImageSize)
            } catch (error) {
              return await fileToDataUrl(file)
            }
          } else {
            return file
          }
        })
      )
      const formImages: ImageProps[] = files.map((file, index) => {
        if (file instanceof File) {
          return {
            image: result[index] || '',
            file_name: file.name,
          } as ImageProps
        } else {
          return file
        }
      })
      onChangeImagesRef.current(
        formImages.map((image: ImageProps) => ({
          ...image,
          file: undefined,
        })),
        // NOTE: 画像がある時か、削除操作した時だけバリデーションする
        !!(isEmptied || formImages.length)
      )
    }
    setConvertedImages()
    return () => {
      files.forEach((file) => {
        if (file instanceof File) {
          URL.revokeObjectURL(URL.createObjectURL(file))
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  return (
    <div className={classes.root}>
      <div className={classes.description}>
        {(injectedErrorMessage || errorMessage || description) && (
          <Caption error={!!(injectedErrorMessage || errorMessage)}>
            {injectedErrorMessage || errorMessage || description || null}
          </Caption>
        )}
      </div>
      <Grid container spacing={2}>
        {files &&
          files.map((image, index) => {
            return (
              <Grid item key={`uploaded-files-${index}`}>
                <Thumbnail
                  image={image}
                  onDelete={() => onDeleteHandler(index)}
                />
              </Grid>
            )
          })}
        {uploadable && (
          <Grid item key={'dropzone'}>
            <Paper
              variant="outlined"
              square
              className={cx(
                classes.dropzone,
                !files.length && required && classes.required
              )}
            >
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <div className={classes.dropzoneText}>
                  <Trans
                    i18nKey="trans.ファイルをドラッグアンドドロップまたは..."
                    components={{
                      text1: <Text1 className={classes.dropzoneTextTitle} />,
                      caption1: <Caption />,
                      caption2: <Caption className={classes.highlightText} />,
                    }}
                  />
                </div>
              </div>
            </Paper>
          </Grid>
        )}
      </Grid>
    </div>
  )
}

EditableThumbnailList.defaultProps = {
  maxLength: 5,
  maxFileSize: 5 * 1024 * 1024, // 5MB
  maxImageSize: 1200,
}
